import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Languages } from 'lucide-react';

import { loadTranslationCatalogue } from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, SummaryBar, SummaryItem,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel, statusLabel } from './labels';

const NUMBER = new Intl.NumberFormat();

function typeTone(type) {
  if (type.status !== 'active') return 'warn';
  if (type.pipeline?.status && type.pipeline.status !== 'online') return 'warn';
  return 'ok';
}

function ContentTypes({ rows, languageTotal, referenceLanguage }) {
  if (rows.length === 0) return <Empty title={tx("ui.content.no_content_types_match_cca12bca")} />;
  return (
    <div className="xo-table-wrap">
      <table className="xo-table xo-content-types-table">
        <thead>
          <tr>
            <th>{tx("ui.content.content_4f9be057")}</th><th>{tx("ui.content.kind_e00ac23f")}</th><th>{tx("ui.content.status_bae7d5be")}</th><th>{tx("ui.content.pipeline_32b1d5a7")}</th>
            <th className="xo-num">{tx("ui.content.keys_e5651c68")}</th><th className="xo-num">{tx("ui.content.languages_db07be18")}</th>
            <th className="xo-num">{tx("ui.content.entries_f056d0d5")}</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map((type) => (
            <tr key={type.id}>
              <td>
                <span className="xo-content-name">
                  <strong>{type.displayName}</strong>
                  <small>{type.code}</small>
                  {type.description && <small>{type.description}</small>}
                </span>
              </td>
              <td><Pill tone={type.kind === 'report' ? 'info' : 'flat'}>{codeLabel('contentKind', type.kind)}</Pill></td>
              <td><Pill tone={typeTone(type)}>{statusLabel(type.status)}</Pill></td>
              <td>
                {type.pipeline === null ? <span className="xo-muted">{tx("ui.content.not_linked_0196fd47")}</span> : (
                  <span className="xo-content-pipeline">
                    <strong>{type.pipeline.displayName}</strong>
                    <small>
                      {type.pipeline.mode ? tx("ui.content.mode_34eb4cae", { mode: type.pipeline.mode }) : ''}v{type.pipeline.codeVersion}
                      {type.pipeline.status !== 'online' && tx("ui.content.offline_version_8cfde8df")}
                    </small>
                  </span>
                )}
              </td>
              <td className="xo-num">{NUMBER.format(type.keyCount)}</td>
              <td className="xo-num">
                <span className={type.languageCount < languageTotal ? 'xo-content-coverage is-partial' : 'xo-content-coverage'}>
                  {type.languageCount} / {languageTotal}
                </span>
              </td>
              <td className="xo-num">{NUMBER.format(type.translationCount)}</td>
              <td className="xo-content-row-action">
                <Link className="xo-row-chevron"
                  aria-label={tx("ui.content.open_displayname_translations_85e5ebb1", { displayName: type.displayName })}
                  to={`/xomics/content/translations?contentTypeId=${type.id}&languageCode=${encodeURIComponent(referenceLanguage)}`}>
                  <ChevronRight aria-hidden="true" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LanguageRows({ rows }) {
  if (rows.length === 0) return <Empty title={tx("ui.content.no_languages_match_629664a5")} />;
  return (
    <div className="xo-language-grid">
      {rows.map((language) => (
        <Link key={language.code} className="xo-language-row"
          to={`/xomics/content/translations?languageCode=${language.code}`}>
          <span className="xo-language-code">{language.code.toUpperCase()}</span>
          <span className="xo-lines">
            <strong>{language.name}</strong>
            <span>{language.nativeName || '—'}</span>
          </span>
          <span className="xo-language-counts">
            <strong>{NUMBER.format(language.translationCount)}</strong>
            <small>{language.contentTypeCount === 1
              ? tx('ui.content.one_content_type', { count: language.contentTypeCount })
              : tx('ui.content.many_content_types', { count: language.contentTypeCount })}</small>
          </span>
          <ChevronRight className="xo-language-chevron" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

export default function XomicsContent({ view = 'overview' }) {
  const load = useCallback(() => loadTranslationCatalogue(), []);
  const catalogue = useLiveResource(['translations'], load);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');

  const data = catalogue.data || { contentTypes: [], languages: [], referenceLanguage: 'en' };
  const needle = query.trim().toLowerCase();
  const contentTypes = useMemo(() => data.contentTypes.filter((type) => {
    if (kind !== 'all' && type.kind !== kind) return false;
    if (needle === '') return true;
    return [
      type.displayName, type.code, type.description, type.kind,
      type.pipeline?.displayName, type.pipeline?.mode, type.pipeline?.codeVersion,
    ].some((value) => value && String(value).toLowerCase().includes(needle));
  }), [data.contentTypes, kind, needle]);
  const languages = useMemo(() => data.languages.filter((language) => (
    needle === '' || [language.code, language.name, language.nativeName]
      .some((value) => value && String(value).toLowerCase().includes(needle))
  )), [data.languages, needle]);

  if (catalogue.loading && catalogue.data === null) return <Loading label={tx("ui.content.reading_content_catalogue_bbc34aef")} />;
  if (catalogue.error && catalogue.data === null) return <Failed message={catalogue.error} onRetry={catalogue.refresh} />;

  const totalEntries = data.contentTypes.reduce((sum, type) => sum + type.translationCount, 0);
  const linked = data.contentTypes.filter((type) => type.pipeline !== null).length;
  const staleLinks = data.contentTypes.filter((type) => type.pipeline !== null && type.pipeline.status !== 'online').length;
  const title = view === 'languages' ? tx('ui.content.page_title_languages') : tx('ui.content.page_title');
  const subtitle = view === 'languages' ? tx('ui.content.page_subtitle_languages') : tx('ui.content.page_subtitle');

  return (
    <div className="xo-platform-page">
      <Head title={title} subtitle={subtitle}>
        <Link className="xo-btn" to="/xomics/content/translations"><Languages aria-hidden="true" /> {tx("ui.content.edit_translations_00caae3d")}</Link>
      </Head>

      {catalogue.error && <p className="xo-alert t-warn" role="alert">{catalogue.error}</p>}

      <SummaryBar>
        <SummaryItem value={data.contentTypes.length} label={tx("ui.content.content_types_95c04d94")}
          detail={tx("ui.content.length_reports_length2_services_91c512f5", { length: data.contentTypes.filter((type) => type.kind === 'report').length, length2: data.contentTypes.filter((type) => type.kind === 'service').length })} />
        <SummaryItem value={data.languages.length} label={tx("ui.content.active_languages_04aa7fc5")}
          detail={tx("ui.content.value_reference_7b639c2a", { value: String(data.referenceLanguage || 'en').toUpperCase() })} />
        <SummaryItem value={NUMBER.format(totalEntries)} label={tx("ui.content.translation_entries_1d4bd745")}
          detail={tx("ui.content.stored_across_every_namespace_02f4699d")} />
        <SummaryItem value={linked} label={tx("ui.content.pipeline_links_f7f0154f")} tone={staleLinks > 0 ? 'warn' : 'ok'}
          detail={staleLinks > 0 ? tx("ui.content.stalelinks_linked_to_an_offline_version_0cf1ea86", { staleLinks: staleLinks }) : tx("ui.content.all_linked_versions_online_194b50a8")} />
      </SummaryBar>

      <div className="xo-content-toolbar">
        <SearchField value={query} onChange={setQuery}
          placeholder={tx("ui.content.search_content_pipeline_or_language_1bac35cf")}
          label={tx("ui.content.search_content_catalogue_2c469e31")} />
        {view !== 'languages' && (
          <div className="xo-seg" role="group" aria-label={tx("ui.content.content_kind_9a09b80c")}>
            {['all', 'report', 'service'].map((value) => (
              <button type="button" key={value} className={kind === value ? 'is-on' : ''}
                onClick={() => setKind(value)}>{value === 'all' ? tx("ui.content.all_6a720856") : tx("ui.content.valuevalue2s_864c2984", { value: value[0].toUpperCase(), value2: value.slice(1) })}</button>
            ))}
          </div>
        )}
      </div>

      {view === 'overview' && (
        <section className="xo-catalogue-section">
          <div className="xo-section-heading"><div><h2>{tx("ui.content.content_types_20018f67")}</h2>
            <p>{tx("ui.content.the_exact_pipeline_version_language_coverage_and_store_4a8e81b5")}</p></div>
            <span>{tx("ui.content.length_of_length2_b1f4e898", { length: contentTypes.length, length2: data.contentTypes.length })}</span></div>
          <div className="xo-data-surface"><ContentTypes rows={contentTypes} languageTotal={data.languages.length}
            referenceLanguage={data.referenceLanguage || 'en'} /></div>
        </section>
      )}

      {(view === 'overview' || view === 'languages') && (
        <section className="xo-catalogue-section">
          <div className="xo-section-heading"><div><h2>{tx("ui.content.language_coverage_44f05796")}</h2>
            <p>{tx("ui.content.real_stored_translations_rather_than_configured_langua_74d83ab5")}</p></div>
            <span>{tx("ui.content.length_of_length2_b1f4e898", { length: languages.length, length2: data.languages.length })}</span></div>
          <div className="xo-data-surface is-padded"><LanguageRows rows={languages} /></div>
        </section>
      )}
    </div>
  );
}
